import Reveal from "@/components/site/Reveal";

/**
 * ChapterDivider → Vogue-Kapitel: Full-Bleed-Foto mit Serifen-Headline,
 * Gold-Akzentwort in Kursive, Gold-Hairline und Zitat.
 * Ohne photo-Prop: typografische Cream-Szene im gleichen Stil.
 */
export default function ChapterDivider({ n, kicker, title, quote, attribute, photo, layout }) {
  const titleLines = String(title).replace(/\.$/, "").split(" ");
  const last = titleLines.pop();
  const first = titleLines.join(" ");

  if (photo && layout === "split") {
    return (
      <section data-testid={`chapter-${n}`} className="relative overflow-hidden" style={{ backgroundColor: "#F3E3D3" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center py-20 md:py-28">
          <Reveal>
            <div>
              <div className="flex items-center gap-3.5 flex-wrap">
                <span className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-sand-500">
                  Dickies-Campus · MMXXVI
                </span>
                <span className="h-px w-11 bg-terra/60" />
                <span className="text-[10.5px] uppercase tracking-[0.32em] font-bold text-terra">
                  {kicker}
                </span>
              </div>
              <h2 className="mt-7 font-magazine font-medium leading-[1.05] text-sand-800 text-[2.9rem] sm:text-[4rem] lg:text-[4.6rem]">
                {first && <span className="block">{first}</span>}
                <span className="block italic text-terra">{last}.</span>
              </h2>
              <span className="block h-[2px] w-14 bg-saffron my-8" />
              {quote && (
                <p className="font-magazine italic max-w-xl text-sand-700 text-[17px] md:text-[19px] leading-[1.7]">
                  „{quote}"
                  {attribute && <span className="not-italic font-heading block mt-4 text-[11px] uppercase tracking-[0.26em] font-bold text-terra">{attribute}</span>}
                </p>
              )}
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="relative max-w-md lg:max-w-none mx-auto lg:mr-4">
              <img
                src={photo}
                alt=""
                loading="lazy"
                className="w-full aspect-[4/5] object-cover shadow-[0_36px_80px_-32px_rgba(82,42,37,0.45)]"
              />
              <div className="absolute -bottom-4 -right-4 h-full w-full border border-terra/40 pointer-events-none" aria-hidden="true" />
              <span className="absolute top-4 right-5 text-[10px] uppercase tracking-[0.3em] font-semibold text-cream-50/90">
                Szene {n}
              </span>
            </div>
          </Reveal>
        </div>
      </section>
    );
  }

  if (photo) {
    return (
      <section data-testid={`chapter-${n}`} className="relative overflow-hidden bg-espresso-deep">
        <div className="relative min-h-[560px] md:min-h-[660px] flex items-center">
          <img
            src={photo}
            alt=""
            className="absolute inset-0 w-full h-full object-cover scene-kenburns"
            loading="lazy"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(100deg, rgba(30,22,16,0.55) 0%, rgba(30,22,16,0.2) 55%, rgba(30,22,16,0.04) 100%)" }} />
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none opacity-[0.10] mix-blend-overlay"
            style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }}
          />

          <div className="absolute top-8 right-6 lg:right-10 text-[10.5px] uppercase tracking-[0.3em] font-semibold text-saffron">
            Szene {n}
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
            <Reveal>
              <div className="max-w-3xl">
                <div className="flex items-center gap-3.5 flex-wrap">
                  <span className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-cream-100/75">
                    Dickies-Campus · MMXXVI
                  </span>
                  <span className="h-px w-11 bg-saffron" />
                  <span className="text-[10.5px] uppercase tracking-[0.32em] font-bold text-saffron">
                    {kicker}
                  </span>
                </div>
                <h2 className="mt-7 font-magazine font-medium leading-[1.05] text-cream-50 text-[2.9rem] sm:text-[4rem] lg:text-[4.8rem]">
                  {first && <span className="block">{first}</span>}
                  <span className="block italic text-saffron">{last}.</span>
                </h2>
                <span className="block h-[2px] w-14 bg-saffron my-8" />
                {quote && (
                  <p className="font-magazine italic max-w-xl text-cream-100/90 text-[17px] md:text-[19px] leading-[1.7]">
                    „{quote}"
                    {attribute && <span className="not-italic font-heading block mt-4 text-[11px] uppercase tracking-[0.26em] font-bold text-saffron">{attribute}</span>}
                  </p>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid={`chapter-${n}`} className="relative bg-cream-50 py-24 md:py-36 border-y border-cream-300 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex items-center gap-3.5 flex-wrap">
            <span className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-sand-500">
              Dickies-Campus · MMXXVI
            </span>
            <span className="h-px w-11 bg-saffron" />
            <span className="text-[10.5px] uppercase tracking-[0.32em] font-bold text-terra">{kicker}</span>
            <span className="ml-auto text-[10.5px] uppercase tracking-[0.3em] font-semibold text-saffron">Szene {n}</span>
          </div>
          <h2 className="mt-7 font-magazine font-medium leading-[1.05] text-sand-800 text-[2.9rem] sm:text-[4rem] lg:text-[4.8rem]">
            {first && <span className="block">{first}</span>}
            <span className="block italic text-terra">{last}.</span>
          </h2>
          <span className="block h-[2px] w-14 bg-saffron my-8" />
          {quote && (
            <p className="font-magazine italic max-w-xl text-sand-600 text-[17px] md:text-[19px] leading-[1.7]">
              „{quote}"
              {attribute && <span className="not-italic font-heading block mt-4 text-[11px] uppercase tracking-[0.26em] font-bold text-terra">{attribute}</span>}
            </p>
          )}
        </Reveal>
      </div>
    </section>
  );
}
